import { Check, ChevronDown } from "lucide-react";
import { compareWorkspaces } from "@tc-superpods/app-contract";
import { useState, type JSX } from "react";
import { BrandMark, GridDotsIcon } from "./BrandMark";
import { cn } from "./lib/utils";
import { NavIcon } from "./NavIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import type { Workspace } from "../types";

export interface WorkspaceSwitcherProps {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  hovered: boolean;
  collapsed: boolean;
  onSelectWorkspace: (workspaceId: string) => void;
}

export function WorkspaceSwitcher({
  workspaces,
  activeWorkspaceId,
  hovered,
  collapsed,
  onSelectWorkspace
}: WorkspaceSwitcherProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const showGrid = hovered || open;
  const sortedWorkspaces = [...workspaces].sort(compareWorkspaces);
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId);

  const markClasses = cn(
    "flex size-8 shrink-0 items-center justify-center rounded-sm text-sidebar-text transition-colors",
    showGrid && "bg-sidebar-surface-hover"
  );

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="Change workspace"
        data-platform-workspace-switcher=""
        className={cn(
          // The card carries the Figma workspace-switcher fill and border; the
          // collapsed rail keeps just the mark.
          "flex items-center gap-2 font-nav text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed
            ? "justify-center rounded-md"
            : "w-full rounded-card border border-shell-card-border bg-shell-card p-3"
        )}
      >
        {collapsed ? (
          <span className={markClasses}>{showGrid ? <GridDotsIcon /> : <BrandMark />}</span>
        ) : (
          // The Kore OS mark (65x32) lives on the CDN at icons/k_os.svg; local and
          // test shells serve the same file from apps/shell/public/icons.
          <img
            src={`${__KORENEXT_ICONS_BASE_URL__}/k_os.svg`}
            alt="Kore OS"
            width={65}
            height={32}
            data-platform-workspace-icon=""
            className="h-8 w-auto shrink-0"
          />
        )}
        {!collapsed && (
          <>
            <span className="line-clamp-2 min-w-0 flex-1 font-display text-lg leading-none uppercase text-sidebar-text">
              {activeWorkspace?.label ?? "Workspace"}
            </span>
            <ChevronDown className="size-6 shrink-0 text-sidebar-text" strokeWidth={1.5} aria-hidden="true" />
          </>
        )}
      </button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right">Change workspace</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}

      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>Workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sortedWorkspaces.map((workspace) => {
          const isActive = workspace.id === activeWorkspaceId;

          return (
            <DropdownMenuItem
              key={workspace.id}
              data-platform-workspace-choice={workspace.id}
              onSelect={() => onSelectWorkspace(workspace.id)}
              className={cn(isActive && "font-medium")}
            >
              <Check className={cn(isActive ? "opacity-100" : "opacity-0")} aria-hidden="true" />
              {workspace.icon ? <NavIcon icon={workspace.icon} className="size-4 shrink-0 object-contain" /> : null}
              <span>{workspace.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
